import type {
  AnnouncementAudience,
  AnnouncementDraft,
  AnnouncementRow,
} from '../../../lib/notifications/announcements';
import type { Client } from '@/types';
import { activityService } from './activityService';
import { clientAccess } from './clientAccess';

/*
 * Announcements to the people on a Numa workspace.
 *
 * Each announcement is one row in the workspace's own notifications table
 * (lib/notifications/announcements.ts has the row and the rules). The portal
 * writes it there through the client access Lambda (`announcements.*`), which
 * builds every row itself, and reads it back from there: the workspaces hold
 * the only copy, so what this page lists is what people see. Posting the same
 * announcement to many workspaces writes the same id to each, and the list
 * merges them back into one.
 */

export const ANNOUNCEMENTS_CONCURRENCY = 8;

/** A workspace this page can reach: a real client with an account on record. */
export const isReachableClient = (client: Client): boolean =>
  client.name !== 'platform-settings' &&
  Boolean(client.config?.clientAccountId) &&
  client.config.clientAccountId !== 'unknown';

/**
 * The error as the page shows it. The client access Lambda already words its
 * refusals for the page; the names below cover an AWS error that reaches here
 * with its name. `conflict` is what a failed condition means for the write
 * that failed: already there, or not there.
 */
export const describeError = (error: unknown, conflict = 'The announcement is not on this workspace.'): string => {
  const name = (error as { name?: string } | null)?.name;
  if (name === 'ResourceNotFoundException') return 'This workspace has no notifications table.';
  if (name === 'ConditionalCheckFailedException') return conflict;
  if (name === 'AccessDenied' || name === 'AccessDeniedException')
    return 'The portal could not sign in to this workspace account.';
  return error instanceof Error ? error.message : String(error);
};

/** Run `task` over every item, at most `limit` at a time, in the input order. */
export const mapWithConcurrency = async <T, R>(
  items: T[],
  limit: number,
  task: (item: T) => Promise<R>
): Promise<R[]> => {
  const results = new Array<R>(items.length);
  let next = 0;
  const worker = async (): Promise<void> => {
    while (next < items.length) {
      const index = next;
      next += 1;
      results[index] = await task(items[index]);
    }
  };
  await Promise.all(Array.from({ length: Math.min(limit, items.length) }, () => worker()));
  return results;
};

// ─── Can the workspace show announcements yet? ─────────────────────────────

/**
 * - `ready`: the workspace's server has the announcements routes, so people
 *   see a posted one on their next visit.
 * - `outdated`: its server predates announcements. A posted one is stored and
 *   waits; people see it once the workspace is deployed with them.
 * - `unknown`: the check itself failed. Nothing is claimed either way.
 */
export type WorkspaceReadiness = 'ready' | 'outdated' | 'unknown';

const READINESS_TTL_MS = 5 * 60 * 1000;
const readinessCache = new Map<string, { value: WorkspaceReadiness; at: number }>();

/** Tests only. */
export const clearReadinessCache = (): void => readinessCache.clear();

/**
 * Ask the workspace's own notifications-api Lambda for the announcements list,
 * through the client access Lambda (`announcements.readiness`). A server with
 * the feature answers 200; one without answers 404. Remembered for five
 * minutes, so a workspace deployed meanwhile is noticed on the next check.
 */
export const checkWorkspaceReadiness = async (client: Client): Promise<WorkspaceReadiness> => {
  const cached = readinessCache.get(client.name);
  if (cached && Date.now() - cached.at < READINESS_TTL_MS) return cached.value;
  let value: WorkspaceReadiness = 'unknown';
  try {
    const { readiness } = await clientAccess<{ readiness: WorkspaceReadiness }>('announcements.readiness', {
      clientName: client.name,
    });
    value = readiness === 'ready' || readiness === 'outdated' ? readiness : 'unknown';
  } catch {
    value = 'unknown';
  }
  if (value !== 'unknown') readinessCache.set(client.name, { value, at: Date.now() });
  return value;
};

/** Ask each workspace, 8 at a time. For the review step before sending. */
export const checkReadinessOf = async (clients: Client[]): Promise<Record<string, WorkspaceReadiness>> => {
  const answers = await mapWithConcurrency(clients, ANNOUNCEMENTS_CONCURRENCY, async (client) => ({
    name: client.name,
    readiness: await checkWorkspaceReadiness(client),
  }));
  return Object.fromEntries(answers.map((a) => [a.name, a.readiness]));
};

export const WAITING_FOR_UPDATE =
  'Stored. People see it once this workspace is deployed with a version of Numa that has announcements.';

// ─── Reading the fleet ──────────────────────────────────────────────────────

export interface WorkspaceCopy {
  clientName: string;
  seenCount: number;
  /** Set while it is revoked on this workspace. */
  revokedAt?: number;
  revokedBy?: string;
  /** Whether people on this workspace can see announcements yet. */
  readiness?: WorkspaceReadiness;
}

/** One announcement, merged across every workspace that holds it. */
export interface FleetAnnouncement {
  id: string;
  title: string;
  body: string;
  linkUrl?: string;
  linkLabel?: string;
  audience: AnnouncementAudience;
  publishedAt: number;
  showUntil: number;
  updatedAt?: number;
  postedBy?: string;
  sentTo?: string;
  workspaces: WorkspaceCopy[];
  totalSeen: number;
  /** Workspaces where it is revoked. Usually all or none; some means a
   *  revoke or restore reached only part of them. */
  revokedOn: string[];
  /** Workspaces that hold it but cannot show it until they are updated. */
  waitingOn: string[];
  /** The latest revoke across the workspaces, for the list. */
  revokedAt?: number;
  revokedBy?: string;
}

export interface FleetReadResult {
  announcements: FleetAnnouncement[];
  /** Workspaces the page could not read, and why. */
  failures: { clientName: string; error: string }[];
  checked: number;
}

const readAnnouncementRows = async (client: Client): Promise<AnnouncementRow[]> => {
  const { rows } = await clientAccess<{ rows: AnnouncementRow[] }>('announcements.list', { clientName: client.name });
  return rows;
};

/**
 * Merge each workspace's rows into one entry per announcement. The newest
 * edit wins where copies disagree (an edit that reached only some of them).
 */
export const mergeFleetRows = (
  perClient: { clientName: string; rows: AnnouncementRow[]; readiness?: WorkspaceReadiness }[]
): FleetAnnouncement[] => {
  const byId = new Map<string, { latest: AnnouncementRow; workspaces: WorkspaceCopy[] }>();
  for (const { clientName, rows, readiness } of perClient) {
    for (const row of rows) {
      if (!row.notification_id || !row.title) continue;
      const entry = byId.get(row.notification_id);
      const copy: WorkspaceCopy = {
        clientName,
        seenCount: Number(row.seen_count) || 0,
        ...(typeof row.revoked_at === 'number' ? { revokedAt: row.revoked_at } : {}),
        ...(row.revoked_by ? { revokedBy: row.revoked_by } : {}),
        ...(readiness ? { readiness } : {}),
      };
      if (!entry) {
        byId.set(row.notification_id, { latest: row, workspaces: [copy] });
        continue;
      }
      entry.workspaces.push(copy);
      const stamp = (r: AnnouncementRow): number => r.updated_at ?? r.published_at ?? 0;
      if (stamp(row) > stamp(entry.latest)) entry.latest = row;
    }
  }
  return [...byId.entries()]
    .map(([id, { latest, workspaces }]) => {
      const revoked = workspaces.filter((w) => w.revokedAt !== undefined);
      const lastRevoke = revoked.reduce<WorkspaceCopy | undefined>(
        (best, w) => (!best || (w.revokedAt ?? 0) > (best.revokedAt ?? 0) ? w : best),
        undefined
      );
      return {
        id,
        title: latest.title,
        body: latest.body,
        ...(latest.link_url && latest.link_label ? { linkUrl: latest.link_url, linkLabel: latest.link_label } : {}),
        audience: latest.audience === 'admins' ? ('admins' as const) : ('everyone' as const),
        publishedAt: latest.published_at,
        showUntil: latest.show_until,
        ...(latest.updated_at ? { updatedAt: latest.updated_at } : {}),
        ...(latest.posted_by ? { postedBy: latest.posted_by } : {}),
        ...(latest.sent_to ? { sentTo: latest.sent_to } : {}),
        workspaces: workspaces.sort((a, b) => a.clientName.localeCompare(b.clientName)),
        totalSeen: workspaces.reduce((sum, w) => sum + w.seenCount, 0),
        revokedOn: revoked.map((w) => w.clientName).sort(),
        waitingOn: workspaces
          .filter((w) => w.readiness === 'outdated')
          .map((w) => w.clientName)
          .sort(),
        ...(lastRevoke?.revokedAt ? { revokedAt: lastRevoke.revokedAt } : {}),
        ...(lastRevoke?.revokedBy ? { revokedBy: lastRevoke.revokedBy } : {}),
      };
    })
    .sort((a, b) => b.publishedAt - a.publishedAt);
};

export const readFleetAnnouncements = async (
  clients: Client[],
  onProgress?: (checked: number, total: number) => void
): Promise<FleetReadResult> => {
  const targets = clients.filter(isReachableClient);
  let checked = 0;
  const results = await mapWithConcurrency(targets, ANNOUNCEMENTS_CONCURRENCY, async (client) => {
    try {
      const rows = await readAnnouncementRows(client);
      // Only worth asking where something is waiting to be seen.
      const readiness = rows.length > 0 ? await checkWorkspaceReadiness(client) : undefined;
      return { clientName: client.name, rows, readiness };
    } catch (error) {
      return { clientName: client.name, error: describeError(error) };
    } finally {
      checked += 1;
      onProgress?.(checked, targets.length);
    }
  });
  return {
    announcements: mergeFleetRows(
      results.filter(
        (r): r is { clientName: string; rows: AnnouncementRow[]; readiness: WorkspaceReadiness | undefined } =>
          'rows' in r
      )
    ),
    failures: results
      .filter((r): r is { clientName: string; error: string } => 'error' in r)
      .sort((a, b) => a.clientName.localeCompare(b.clientName)),
    checked: targets.length,
  };
};

// ─── Writing to workspaces ──────────────────────────────────────────────────

/** `waiting`: written, but the workspace cannot show it until it is updated. */
export type WorkspaceRunState = 'queued' | 'running' | 'success' | 'waiting' | 'failed';

export interface WorkspaceRunStatus {
  state: WorkspaceRunState;
  message?: string;
}

export type WorkspaceRunUpdate = (clientName: string, status: WorkspaceRunStatus) => void;

export interface WorkspaceRunSummary {
  succeeded: string[];
  /** Of `succeeded`: stored, but the workspace predates announcements. */
  waiting: string[];
  failed: { clientName: string; error: string }[];
}

type AnnouncementAction = 'posted' | 'edited' | 'revoked' | 'restored';

const CONFLICT: Record<AnnouncementAction, string> = {
  posted: 'It is already on this workspace.',
  edited: 'The announcement is not on this workspace.',
  revoked: 'The announcement is not on this workspace.',
  restored: 'The announcement is not on this workspace.',
};

/** One write per workspace, `ANNOUNCEMENTS_CONCURRENCY` at a time, each logged
 *  to the portal's Activity page whether it worked or not. */
const runOnWorkspaces = async (
  clients: Client[],
  action: AnnouncementAction,
  announcementId: string,
  title: string,
  write: (client: Client) => Promise<void>,
  onUpdate?: WorkspaceRunUpdate,
  /** Say whether people can see it yet. Not worth asking for a revoke. */
  checkReadiness = true
): Promise<WorkspaceRunSummary> => {
  const summary: WorkspaceRunSummary = { succeeded: [], waiting: [], failed: [] };
  clients.forEach((client) => onUpdate?.(client.name, { state: 'queued' }));
  await mapWithConcurrency(clients, ANNOUNCEMENTS_CONCURRENCY, async (client) => {
    onUpdate?.(client.name, { state: 'running' });
    try {
      if (!isReachableClient(client)) throw new Error('This workspace has no AWS account on record.');
      await write(client);
      summary.succeeded.push(client.name);
      const readiness = checkReadiness ? await checkWorkspaceReadiness(client) : 'ready';
      if (readiness === 'outdated') {
        summary.waiting.push(client.name);
        onUpdate?.(client.name, { state: 'waiting', message: WAITING_FOR_UPDATE });
      } else {
        onUpdate?.(client.name, {
          state: 'success',
          ...(readiness === 'unknown' ? { message: 'Stored. Could not check whether people can see it yet.' } : {}),
        });
      }
      await activityService.logActivity({
        type: 'system',
        action,
        resourceType: 'announcement',
        resourceId: client.name,
        details: { metadata: { announcementId, title } },
        success: true,
      });
    } catch (error) {
      const message = describeError(error, CONFLICT[action]);
      summary.failed.push({ clientName: client.name, error: message });
      onUpdate?.(client.name, { state: 'failed', message });
      await activityService.logActivity({
        type: 'system',
        action,
        resourceType: 'announcement',
        resourceId: client.name,
        details: { metadata: { announcementId, title, error: message } },
        success: false,
        errorMessage: message,
      });
    }
  });
  summary.succeeded.sort();
  summary.waiting.sort();
  summary.failed.sort((a, b) => a.clientName.localeCompare(b.clientName));
  return summary;
};

/** What the list says it was sent to. `extra` counts the workspaces picked
 *  by hand on top of the groups. */
export const describeTargets = (groups: string[], clientCount: number, extra = 0): string => {
  const workspaces = `${clientCount} workspace${clientCount === 1 ? '' : 's'}`;
  if (groups.length === 0) return workspaces;
  const named = `${groups.length === 1 ? 'Deployment group' : 'Deployment groups'}: ${groups.join(', ')}`;
  return `${named}${extra > 0 ? `, plus ${extra} more` : ''} (${workspaces})`;
};

export const newAnnouncementId = (): string => `ann_${crypto.randomUUID()}`;

/** The draft fields only, so nothing else the page holds is sent. */
const draftFields = (draft: AnnouncementDraft): AnnouncementDraft => ({
  title: draft.title,
  body: draft.body,
  ...(draft.linkUrl !== undefined ? { linkUrl: draft.linkUrl } : {}),
  ...(draft.linkLabel !== undefined ? { linkLabel: draft.linkLabel } : {}),
  audience: draft.audience,
  showUntil: draft.showUntil,
});

/**
 * Post a new announcement to each workspace. Writing the same id twice is
 * refused. The Lambda builds the row and records the person signed in as the
 * poster. One time for the whole run, so every workspace's copy has the same
 * posted date.
 */
export const postAnnouncement = async (
  clients: Client[],
  id: string,
  draft: AnnouncementDraft,
  meta: { sentTo?: string },
  onUpdate?: WorkspaceRunUpdate
): Promise<WorkspaceRunSummary> => {
  const publishedAt = Date.now();
  return runOnWorkspaces(
    clients,
    'posted',
    id,
    draft.title.trim(),
    async (client) => {
      await clientAccess('announcements.post', {
        clientName: client.name,
        id,
        draft: draftFields(draft),
        meta: { publishedAt, ...(meta.sentTo ? { sentTo: meta.sentTo } : {}) },
      });
    },
    onUpdate
  );
};

/**
 * Change the words, the link, the audience or the date on every workspace
 * that holds it. People who already closed it are not shown it again; the
 * seen count and the posted date stay as they were.
 */
export const editAnnouncement = async (
  clients: Client[],
  id: string,
  draft: AnnouncementDraft,
  onUpdate?: WorkspaceRunUpdate
): Promise<WorkspaceRunSummary> => {
  const updatedAt = Date.now();
  return runOnWorkspaces(
    clients,
    'edited',
    id,
    draft.title.trim(),
    async (client) => {
      await clientAccess('announcements.edit', { clientName: client.name, id, draft: draftFields(draft), updatedAt });
    },
    onUpdate
  );
};

/**
 * Revoke it on every workspace: nobody sees it again, as a pop-up or in the
 * history. The row stays, so this page still lists it, and Restore brings it
 * back. The table's clean-up date is unchanged. The Lambda records the person
 * signed in as the one who revoked it.
 */
export const revokeAnnouncement = async (
  clients: Client[],
  id: string,
  title: string,
  onUpdate?: WorkspaceRunUpdate
): Promise<WorkspaceRunSummary> =>
  runOnWorkspaces(
    clients,
    'revoked',
    id,
    title,
    async (client) => {
      await clientAccess('announcements.revoke', { clientName: client.name, id });
    },
    onUpdate,
    false
  );

/** Undo a revoke: people see it again in the history, and as a pop-up if it
 *  is still inside its date and they have not closed it before. */
export const restoreAnnouncement = async (
  clients: Client[],
  id: string,
  title: string,
  onUpdate?: WorkspaceRunUpdate
): Promise<WorkspaceRunSummary> =>
  runOnWorkspaces(
    clients,
    'restored',
    id,
    title,
    async (client) => {
      await clientAccess('announcements.restore', { clientName: client.name, id });
    },
    onUpdate
  );
